import { useEffect, useRef, useState } from "react";
import { preloadImages } from "./imageCache";

interface PagePreloadState {
  ready: boolean;
  loaded: number;
  total: number;
}

// Preloads every image a page needs and reports back once they've all
// settled (loaded or failed) so the page can stay on a skeleton until then.
// Re-runs whenever the url list identity changes (e.g. category param switch).
export function usePagePreload(urls: string[]): PagePreloadState {
  const [state, setState] = useState<PagePreloadState>(() => ({
    ready: urls.length === 0,
    loaded: 0,
    total: urls.length,
  }));
  const key = urls.join("|");
  const keyRef = useRef(key);

  useEffect(() => {
    keyRef.current = key;
    let cancelled = false;
    let loaded = 0;

    if (urls.length === 0) {
      setState({ ready: true, loaded: 0, total: 0 });
      return;
    }

    setState({ ready: false, loaded: 0, total: urls.length });

    urls.forEach((url) => {
      preloadImages([url]).then(() => {
        if (cancelled || keyRef.current !== key) return;
        loaded += 1;
        setState({ ready: loaded === urls.length, loaded, total: urls.length });
      });
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
